import { Icon, ProfilePicture } from "@sellar-io/components";
import dayjs from "dayjs";
import { DIRECT_MESSAGE, DIRECT_MESSAGE_LIST } from "../../../constants/routes";
import { useMessageInboxList } from "../../../hooks/api/v2/message/useMessageInboxList";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { isHttpError } from "../../../utils/httpClient";
import {
  NavigationOverlayBody,
  NavigationOverlayFooter,
  NavigationOverlayHeader,
} from "./NavigationOverlayLayout";
import { NavigationOverlayRow } from "./NavigationOverlayRow";

const getMessageTime = (createdAt: Date) => {
  const date = dayjs(createdAt);
  const now = dayjs();
  if (date.isSame(now, "day")) {
    return date.format("HH:mm");
  }
  if (date.isSame(now.subtract(1, "day"), "day")) {
    return "Yesterday";
  }
  return date.format("D MMM");
};

export const MessagesNavigationContent = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  if (business?.type !== "retailer" || !business.Retailer?.id) {
    return (
      <>
        <NavigationOverlayHeader title="Messages" />
        <p className="py-4 text-sm text-zinc-500">
          Messages are unavailable for this business.
        </p>
      </>
    );
  }
  return <BuyerMessages key={business.id} onClose={onClose} />;
};

const BuyerMessages = ({ onClose }: { onClose: () => void }) => {
  const query = useMessageInboxList(
    { limit: 3 },
    {
      retry: (failureCount, error) => {
        const status = error.response?.status;
        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
  );
  const navigateToChild = useNavigateToChild();
  const messages = query.data?.pages[0] ?? [];
  const permissionDenied =
    isHttpError(query.error) &&
    (query.error.response?.status === 401 ||
      query.error.response?.status === 403);
  return (
    <>
      <NavigationOverlayHeader title="Messages" />
      <NavigationOverlayBody>
        {query.isLoading && (
          <p role="status" className="py-4 text-sm text-zinc-500">
            Loading messages…
          </p>
        )}
        {query.isError && (
          <div role="alert" className="py-2 text-sm text-zinc-500">
            {permissionDenied
              ? "You do not have access to messages for this business."
              : "Unable to refresh messages."}
            {!permissionDenied && (
              <button
                type="button"
                onClick={() => {
                  query.refetch();
                }}
                className="ml-1 text-purple-700 underline"
              >
                Try again
              </button>
            )}
          </div>
        )}
        {!query.isPending && !query.isError && messages.length === 0 && (
          <p role="status" className="py-4 text-sm text-zinc-500">
            No conversations yet.
          </p>
        )}
        {!permissionDenied &&
          messages.map((message) => {
            const supplier = message.Relationship.Supplier.Business;
            return (
              <NavigationOverlayRow
                key={message.RelationshipId}
                variant="preview"
                className="shrink-0 items-center"
                onClick={() => {
                  navigateToChild(
                    DIRECT_MESSAGE.href({
                      relationshipId: message.RelationshipId,
                    }),
                  );
                  onClose();
                }}
                leading={
                  <ProfilePicture
                    name={supplier.name}
                    logoUrl={supplier.logoUrl}
                    className="size-10 max-h-10 min-h-10 max-w-10 min-w-10"
                  />
                }
                description={message.message}
                descriptionClassName="line-clamp-2 text-sm leading-5"
                titleClassName="font-semibold"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="min-w-0 flex-1 truncate">
                    {supplier.name}
                  </span>
                  <time
                    dateTime={dayjs(message.createdAt).toISOString()}
                    title={dayjs(message.createdAt).format("D MMM YYYY, HH:mm")}
                    className="shrink-0 text-xs leading-[18px] font-normal text-zinc-500"
                  >
                    {getMessageTime(message.createdAt)}
                  </time>
                </span>
              </NavigationOverlayRow>
            );
          })}
      </NavigationOverlayBody>
      {!permissionDenied && (
        <NavigationOverlayFooter>
          <NavigationOverlayRow
            variant="footer"
            to={DIRECT_MESSAGE_LIST}
            className="py-2 font-semibold text-[#8c23c7]"
            trailing={<Icon icon="CHEVRON_RIGHT" size="16px" />}
          >
            View all messages
          </NavigationOverlayRow>
        </NavigationOverlayFooter>
      )}
    </>
  );
};
